import type { SimulationModification, SimulationResults, SimulationScenario } from '../../types';

// Sample simulation results with comparison data
const createSimulationResults = (
  feasible: boolean,
  conflictCount: number,
  conflictDelta: number,
  teacherLoadDelta: number,
  roomUtilizationDelta: number,
  curriculumCoverageDelta: number,
): SimulationResults => ({
  feasible,
  conflicts: [],
  stats: {
    totalLessons: 248,
    conflictCount,
    ruleViolations: {
      hard: feasible ? 0 : 2,
      soft: 8,
    },
    curriculumCoverage: 98.5 + curriculumCoverageDelta,
    teacherLoad: [],
    roomUtilization: [],
    teacherPreferenceSatisfaction: 87,
    distributionScore: 92,
  },
  comparison: {
    conflictDelta,
    teacherLoadDelta,
    roomUtilizationDelta,
    curriculumCoverageDelta,
  },
  recommendations: feasible
    ? ['Consider adding more science lab time for Year 10', 'Maths lessons could be more evenly distributed']
    : ['Additional rooms required for successful scheduling', 'Teacher availability conflicts need resolution'],
  requiredChanges: feasible ? [] : ['Add part-time science teacher', 'Convert Room 104 to ICT suite'],
});

// Sample modifications for scenarios
const addTeacherModification: SimulationModification = {
  type: 'add',
  entity: 'teacher',
  changes: {
    firstName: 'Sarah',
    lastName: 'Mitchell',
    subjects: ['subject-biology', 'subject-chemistry'],
    contractType: 'full-time',
    maxPeriodsPerWeek: 25,
  },
};

const removeTeacherModification: SimulationModification = {
  type: 'remove',
  entity: 'teacher',
  entityId: 'teacher-015',
  changes: {
    reason: 'Retirement',
    effectiveDate: '2025-08-01',
  },
};

const addRoomModification: SimulationModification = {
  type: 'add',
  entity: 'room',
  changes: {
    name: 'Science Lab 5',
    type: 'laboratory',
    capacity: 30,
    floor: '1',
    building: 'Main Building',
  },
};

const modifyRuleModification: SimulationModification = {
  type: 'modify',
  entity: 'rule',
  entityId: 'rule-max-consecutive',
  changes: {
    previousValue: 3,
    newValue: 4,
    description: 'Increase max consecutive periods from 3 to 4',
  },
};

const addStudentModification: SimulationModification = {
  type: 'add',
  entity: 'student',
  changes: {
    count: 30,
    yearGroup: 'year-7',
    description: 'New Year 7 intake increase',
  },
};

// Sample simulation scenarios
export const mockSimulationScenarios: SimulationScenario[] = [
  {
    id: 'sim-001',
    name: 'New Science Teacher Impact',
    description: 'Assess the impact of adding a new full-time science teacher to cover increased Biology and Chemistry demand',
    baseTimetableId: 'timetable-current',
    modifications: [addTeacherModification],
    status: 'completed',
    results: createSimulationResults(true, 5, -3, -5, 2, 1.5),
    createdAt: '2025-01-15T10:30:00Z',
    createdBy: 'admin-1',
    lastRun: '2025-01-15T11:45:00Z',
  },
  {
    id: 'sim-002',
    name: 'Mr. Johnson Retirement',
    description: 'Model the impact of Mr. Johnson (Maths HoD) retiring at end of term - who can cover his classes?',
    baseTimetableId: 'timetable-current',
    modifications: [removeTeacherModification],
    status: 'completed',
    results: createSimulationResults(false, 12, 4, 8, -2, -3.5),
    createdAt: '2025-01-14T14:00:00Z',
    createdBy: 'admin-1',
    lastRun: '2025-01-14T15:30:00Z',
  },
  {
    id: 'sim-003',
    name: 'Additional Science Lab',
    description: 'Evaluate adding a 5th science laboratory to reduce scheduling conflicts in practical lessons',
    baseTimetableId: 'timetable-current',
    modifications: [addRoomModification],
    status: 'completed',
    results: createSimulationResults(true, 3, -5, 0, 8, 2.0),
    createdAt: '2025-01-13T09:00:00Z',
    createdBy: 'admin-1',
    lastRun: '2025-01-13T10:15:00Z',
  },
  {
    id: 'sim-004',
    name: 'Relaxed Consecutive Period Rule',
    description: 'Test allowing 4 consecutive periods instead of 3 to improve scheduling flexibility',
    baseTimetableId: 'timetable-current',
    modifications: [modifyRuleModification],
    status: 'completed',
    results: createSimulationResults(true, 2, -6, -3, 1, 0.5),
    createdAt: '2025-01-12T16:00:00Z',
    createdBy: 'admin-1',
    lastRun: '2025-01-12T16:45:00Z',
  },
  {
    id: 'sim-005',
    name: 'Year 7 Intake Increase',
    description: 'Model the impact of 30 additional Year 7 students joining in September',
    baseTimetableId: 'timetable-current',
    modifications: [addStudentModification],
    status: 'running',
    createdAt: '2025-01-16T08:30:00Z',
    createdBy: 'admin-1',
  },
  {
    id: 'sim-006',
    name: 'Combined Changes - September Planning',
    description: 'Comprehensive scenario combining new teacher, room conversion, and student intake changes for September term',
    baseTimetableId: 'timetable-current',
    modifications: [addTeacherModification, addRoomModification, addStudentModification],
    status: 'draft',
    createdAt: '2025-01-16T11:00:00Z',
    createdBy: 'admin-1',
  },
];

// Quick stats for the simulations overview
export interface SimulationStats {
  total: number;
  completed: number;
  running: number;
  draft: number;
  failed: number;
  feasibleRate: number;
}

export const getSimulationStats = (scenarios: SimulationScenario[]): SimulationStats => {
  const completed = scenarios.filter((s) => s.status === 'completed');
  const feasible = completed.filter((s) => s.results?.feasible);

  return {
    total: scenarios.length,
    completed: completed.length,
    running: scenarios.filter((s) => s.status === 'running').length,
    draft: scenarios.filter((s) => s.status === 'draft').length,
    failed: scenarios.filter((s) => s.status === 'failed').length,
    feasibleRate: completed.length > 0 ? (feasible.length / completed.length) * 100 : 0,
  };
};

// Modification type labels
export const modificationTypeLabels: Record<SimulationModification['type'], string> = {
  add: 'Add',
  remove: 'Remove',
  modify: 'Modify',
};

export const entityTypeLabels: Record<SimulationModification['entity'], string> = {
  teacher: 'Teacher',
  student: 'Student(s)',
  room: 'Room',
  subject: 'Subject',
  rule: 'Rule',
};

// Status colors and labels
export const statusConfig: Record<
  SimulationScenario['status'],
  { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }
> = {
  draft: { label: 'Draft', color: 'default' },
  running: { label: 'Running', color: 'warning' },
  completed: { label: 'Completed', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
};

// Comparison metric labels
export const comparisonMetricLabels = {
  conflictDelta: 'Conflicts',
  teacherLoadDelta: 'Teacher Load',
  roomUtilizationDelta: 'Room Utilization',
  curriculumCoverageDelta: 'Curriculum Coverage',
};

